/**
 * ThemesListPanel - Theme customizer panel with full editing capabilities
 *
 * Adapted from shadcn-studio for OptiDev with database-first persistence.
 * Features: Color editing, typography controls, radius/spacing/shadow settings.
 */

import { useState, useCallback, useEffect, useRef } from "react";

import {
  ChevronLeft,
  RotateCcw,
  Sun,
  Moon,
  Loader2,
  Trash2,
  Plus,
  Undo2,
  Redo2,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { CopyableCell } from "@/components/ui/copyable-cell";
import { Hint } from "@/components/ui/hint";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useResponsive } from "@/hooks/useResponsive";

import { useThemeEditor } from "@/hooks/use-theme-editor";
import { previewMessage } from "@/lib/visual-editor/post-message";
import axios from "axios";
import { invalidateProjectTheme } from "@/lib/api/project-theme-cache";
import { themesApi } from "@/lib/api/themes";
import { commitsApi } from "@/lib/api/commits";
import { fetchSlideManifest } from "@/lib/slides/slide-manifest";
import { useSlideStore } from "@/stores/slide-store";
import type { ThemeMode, ThemeContent } from "@/types/theme.types";

import {
  ThemeColorPanel,
  ThemePresetSelect,
  ThemeFontSelect,
  SliderWithInput,
  ShadowControl,
  SaveThemeDialog,
  DeleteThemeDialog,
  CreateThemeDialog,
} from "./components";
import {
  sansSerifFonts,
  DEFAULT_FONT_SANS,
  getAppliedThemeFont,
} from "./components/theme-font-select";
import {
  parseCssInput,
  parseShadowVariables,
  parseLetterSpacing,
  generateRandomPreviewColors,
  colorFormatter,
} from "./utils";
import { generateThemeCode } from "./utils/theme-style-generator";

interface ThemesListPanelProps {
  onBack?: () => void;
  onThemeSelect?: (themeId: string) => void;
  previewIframe: HTMLIFrameElement | null | undefined;
  projectId?: string;
  workspaceId: string;
  devContainerId?: string;
  initialThemeId?: string;
  themeToEdit?: ThemeContent | null;
  onThemeImported?: () => void;
  onEditTheme?: (theme: ThemeContent) => void;
  setToolbarVisible?: (visible: boolean) => void;
  /**
   * When true, hides "Apply to Project" button and shows only "Save" buttons.
   * Used in Theme Library settings page where no project context exists.
   */
  standaloneMode?: boolean;
}

export function ThemesListPanel({
  onBack,
  onThemeSelect,
  previewIframe,
  projectId,
  workspaceId,
  devContainerId,
  initialThemeId,
  themeToEdit,
  onThemeImported,
  setToolbarVisible,
  standaloneMode = false,
}: ThemesListPanelProps) {
  const { getIdToken } = useAuth();
  const { toast } = useToast();
  const { isTablet } = useResponsive();

  // Theme editor hook
  const {
    themes,
    selectedTheme,
    selectedThemeId,
    mode,
    styles,
    isLoading,
    isSaving,
    error,
    hasChanges,
    isImportedTheme,
    isScratchTheme,
    canUndo,
    canRedo,
    loadThemes,
    selectTheme,
    setMode,
    updateStyle,
    updateBothModes,
    setLightAndDarkStyles,
    sendPreviewToIframe,
    resetToOriginal,
    clearSelectedTheme,
    importTheme,
    saveAsCustomTheme,
    updateCustomTheme,
    deleteTheme,
    undo,
    redo,
    clearHistory,
  } = useThemeEditor({ workspaceId, previewIframe });

  // Clear visual editor selection and hide toolbar when themes panel mounts
  useEffect(() => {
    if (previewIframe) {
      previewMessage.clearSelection(previewIframe);
    }
    if (setToolbarVisible) {
      setToolbarVisible(false);
    }
  }, []); // Empty deps = runs once on mount

  // Fetch project's current theme and select it from the loaded themes list
  // Auto-select project's current theme on mount
  useEffect(() => {
    const fetchProjectTheme = async () => {
      if (themes.length === 0) return;

      // Skip auto-selection if we have an imported theme (from Agent Chat or CSS import)
      if (isImportedTheme || themeToEdit) return;

      // Use initialThemeId if provided
      if (initialThemeId) {
        selectTheme(initialThemeId);
        return;
      }

      // Skip fetching project theme if no projectId (standalone mode)
      if (!projectId) return;

      // Fetch the project's current applied theme
      const token = await getIdToken();
      if (!token) return;

      try {
        const projectTheme = await themesApi.getProjectTheme(token, projectId);
        if (projectTheme?.themeId) {
          selectTheme(projectTheme.themeId);
        }
      } catch (err) {
        // Project may not have a theme applied yet
      }
    };

    fetchProjectTheme();
  }, [projectId, initialThemeId, themes.length, isImportedTheme, themeToEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Local state
  const [isApplying, setIsApplying] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newThemeName, setNewThemeName] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewColors, setPreviewColors] = useState<string[]>(
    generateRandomPreviewColors,
  );
  const [activeTab, setActiveTab] = useState<string>("colors");
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] =
    useState(false);

  // Check if there are unsaved changes (hasChanges, isScratchTheme, isImportedTheme, or has undo/redo history)
  const hasUnsavedChanges =
    hasChanges || isScratchTheme || isImportedTheme || canUndo || canRedo;

  // Handler for back button - check for unsaved changes first
  const handleBackClick = useCallback(() => {
    if (!onBack) return; // No-op if onBack not provided (standalone mode)
    if (hasUnsavedChanges) {
      setUnsavedChangesDialogOpen(true);
    } else {
      clearHistory();
      onBack();
    }
  }, [hasUnsavedChanges, clearHistory, onBack]);

  // Handler for confirming close without saving
  const handleCloseWithoutSaving = useCallback(() => {
    setUnsavedChangesDialogOpen(false);
    clearHistory();
    onBack?.();
  }, [clearHistory, onBack]);

  // Generate new random colors when Create Theme or Save dialog opens
  useEffect(() => {
    if (createDialogOpen || saveDialogOpen) {
      setPreviewColors(generateRandomPreviewColors());
      setNewThemeName("");
    }
  }, [createDialogOpen, saveDialogOpen]);

  // Track last imported theme to prevent re-processing
  const lastImportedThemeRef = useRef<string | null>(null);

  // Import theme from Agent Chat when themeToEdit is set
  useEffect(() => {
    if (!themeToEdit?.cssVars) return;

    // Generate unique key for this theme to prevent re-import
    const themeKey = JSON.stringify(themeToEdit);
    if (lastImportedThemeRef.current === themeKey) return;

    // Mark as imported
    lastImportedThemeRef.current = themeKey;

    // Import theme styles (replaces all styles completely)
    importTheme(
      themeToEdit.cssVars.light || {},
      themeToEdit.cssVars.dark || {},
    );

    // Switch to colors tab
    setActiveTab("colors");

    // Send preview to iframe and notify parent
    setTimeout(() => {
      sendPreviewToIframe();
      onThemeImported?.();
    }, 100);
  }, [themeToEdit, importTheme, sendPreviewToIframe, onThemeImported]);

  // Handler to regenerate random colors
  const handleShuffleColors = useCallback(() => {
    setPreviewColors(generateRandomPreviewColors());
  }, []);

  // Handler to update a specific color
  const handleColorChange = useCallback((index: number, hex: string) => {
    setPreviewColors((prev) => {
      const newColors = [...prev];
      newColors[index] = hex;
      return newColors;
    });
  }, []);

  // Mode change handler
  const handleModeChange = useCallback(
    (value: string) => {
      if (value) {
        setMode(value as ThemeMode);
      }
    },
    [setMode],
  );

  // Style change handler (single mode)
  const handleStyleChange = useCallback(
    (key: string, value: string) => {
      updateStyle(key, value);
    },
    [updateStyle],
  );

  // Font change handler (both modes)
  const handleFontChange = useCallback(
    (fontType: "font-sans" | "font-serif" | "font-mono", value: string) => {
      updateBothModes({ [fontType]: value });
    },
    [updateBothModes],
  );

  // Radius change handler (both modes)
  const handleRadiusChange = useCallback(
    (value: number) => {
      updateBothModes({ radius: `${value}rem` });
    },
    [updateBothModes],
  );

  // Letter spacing change handler (both modes)
  const handleLetterSpacingChange = useCallback(
    (value: number) => {
      updateBothModes({ "letter-spacing": `${value}em` });
    },
    [updateBothModes],
  );

  // Spacing change handler (both modes)
  const handleSpacingChange = useCallback(
    (value: number) => {
      updateBothModes({ spacing: `${value}rem` });
    },
    [updateBothModes],
  );

  // Shadow change handler
  const handleShadowChange = useCallback(
    (key: string, value: string | number) => {
      if (key === "shadow-color") {
        handleStyleChange(key, value as string);
      } else if (key === "shadow-opacity") {
        handleStyleChange(key, value.toString());
      } else {
        handleStyleChange(key, `${value}px`);
      }
    },
    [handleStyleChange],
  );

  // CSS import handler
  const handleCssImport = useCallback(
    (css: string) => {
      const { lightColors, darkColors } = parseCssInput(css);
      const { lightShadows, darkShadows } = parseShadowVariables(css);
      const letterSpacing = parseLetterSpacing(css);

      // Build complete light and dark styles
      const newLightStyles = {
        ...lightColors,
        ...lightShadows,
        "letter-spacing": letterSpacing,
      };

      const newDarkStyles = {
        ...darkColors,
        ...darkShadows,
      };

      // Use setLightAndDarkStyles to avoid async state issues
      // This sets both modes atomically in a single state update
      setLightAndDarkStyles(newLightStyles, newDarkStyles);

      // Clear selected theme - imported CSS becomes temporary/unsaved
      // User can click "Create Theme" to save it
      clearSelectedTheme();

      toast({
        title: "Theme imported",
        description: "Click 'Create Theme' to save your imported theme",
      });
    },
    [setLightAndDarkStyles, clearSelectedTheme, toast],
  );

  // Apply theme to project (directly or after saving)
  const applyThemeToProject = useCallback(
    async (themeId: string, themeName: string) => {
      // Skip if no projectId (standalone mode)
      if (!projectId) return;

      const token = await getIdToken();
      if (!token) return;

      setIsApplying(true);
      try {
        // Forward `mode` so server-side thumbnail capture renders slides in
        // the same theme mode the user is currently viewing.
        const applyResult = await themesApi.applyTheme(
          token,
          projectId,
          themeId,
          mode,
        );
        onThemeSelect?.(themeId);

        // Auto-create commit after successful theme application
        let commitSuccess = false;
        try {
          const API_URL = import.meta.env.VITE_API_URL || "";

          // Step 1: Generate AI commit message
          let commitMessage = `Applied theme: ${themeName}`;
          try {
            const generateResponse = await axios.post(
              `${API_URL}/api/projects/${projectId}/commits/generate-message`,
              {
                changedFiles: ["src/index.css"],
                additionalContext: `Theme applied: "${themeName}" (themeId: ${themeId}). This updates the CSS variables for colors, fonts, spacing, and other design tokens.`,
                devContainerId,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            );
            commitMessage = generateResponse.data.message;
          } catch (aiError) {
            console.warn(
              "Failed to generate AI commit message, using fallback:",
              aiError,
            );
          }

          // Step 2: Create commit with generated message
          await commitsApi.createCommit(projectId, commitMessage);
          commitSuccess = true;
        } catch (commitError) {
          // Don't fail the theme apply if commit fails, just log it
          console.warn(
            "Failed to auto-create commit after theme apply:",
            commitError,
          );
        }

        // Show success toast after everything is done
        toast({
          title: "Theme applied",
          description: commitSuccess
            ? `"${themeName}" has been applied and committed`
            : `"${themeName}" has been applied to your project`,
        });

        // Clear undo/redo history after successful save
        clearHistory();

        // Refresh the slide manifest so each slide's `thumbnail` field picks
        // up the new UUID filename — the per-slide hook keys on the manifest
        // path and re-fetches the blob automatically.
        if (applyResult?.slideThumbnailsUpdated && devContainerId) {
          useSlideStore.getState().bumpThumbnailRevision();
          fetchSlideManifest({ devContainerId, getIdToken }).catch(() => {
            /* non-blocking */
          });
        }

        // Applied-theme state is shared with the chat theme cards — drop the
        // cached snapshot and let them re-derive, so a previously-applied card
        // stops claiming "Applied to project" (#54).
        invalidateProjectTheme(projectId);
      } catch (err: unknown) {
        // apply-theme now fails loudly when the container can't take the CSS
        // write (paused / still starting / sidecar not ready) instead of
        // returning 200 over an unchanged index.css (#54). Say that in words
        // rather than surfacing a raw "status code 409".
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        const errorMessage =
          status === 409
            ? "Project isn't running yet — reopen the preview to start it, then apply again."
            : err instanceof Error
              ? err.message
              : "Failed to apply theme";
        toast({
          title: status === 409 ? "Project not ready" : "Error",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setIsApplying(false);
      }
    },
    [getIdToken, projectId, toast, onThemeSelect, clearHistory, mode, devContainerId],
  );

  // Handle apply button click
  const handleApplyToProject = useCallback(async () => {
    if (!selectedThemeId) return;

    // If user made changes
    if (hasChanges) {
      // If editing a custom theme, update it directly
      if (selectedTheme?.type === "custom") {
        setIsApplying(true);
        try {
          // Pass silent: true to avoid double toast (we'll show "Theme applied" instead)
          const updated = await updateCustomTheme(true);
          if (updated) {
            await applyThemeToProject(
              selectedThemeId,
              selectedTheme?.name || "Theme",
            );
          }
        } finally {
          setIsApplying(false);
        }
        return;
      }

      // If editing a built-in theme, show save dialog to create new
      setSaveDialogOpen(true);
      return;
    }

    // No changes - apply the selected theme directly
    applyThemeToProject(selectedThemeId, selectedTheme?.name || "Theme");
  }, [
    selectedThemeId,
    selectedTheme,
    hasChanges,
    updateCustomTheme,
    applyThemeToProject,
  ]);

  // Save changes to custom theme (standalone mode - no project apply)
  const handleSaveChangesToCustomTheme = useCallback(async () => {
    const updated = await updateCustomTheme();
    if (updated) {
      // Notify parent that theme was updated
      onThemeSelect?.(selectedThemeId || "");
    }
  }, [updateCustomTheme, onThemeSelect, selectedThemeId]);

  // Save as custom theme and apply it (from built-in theme)
  const handleSaveAsCustom = useCallback(async () => {
    if (!newThemeName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a theme name",
        variant: "destructive",
      });
      return;
    }

    // Pass the custom preview colors to saveAsCustomTheme
    // In standalone mode, show toast; in project mode, silent (we show "Theme applied" later)
    const result = await saveAsCustomTheme(
      newThemeName.trim(),
      previewColors,
      !standaloneMode, // silent in project mode
    );
    if (result) {
      setSaveDialogOpen(false);
      setNewThemeName("");

      if (standaloneMode) {
        // In standalone mode, just notify parent that theme was saved
        onThemeSelect?.(result.themeId || "");
      } else {
        // Apply the newly saved theme to the project
        if (result.themeId) {
          await applyThemeToProject(
            result.themeId,
            result.name || newThemeName.trim(),
          );
        }
      }
    }
  }, [
    newThemeName,
    saveAsCustomTheme,
    toast,
    applyThemeToProject,
    previewColors,
    standaloneMode,
    onThemeSelect,
  ]);

  // Create a new custom theme (from current styles)
  const handleCreateTheme = useCallback(async () => {
    if (!newThemeName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a theme name",
        variant: "destructive",
      });
      return;
    }

    // Pass the custom preview colors to saveAsCustomTheme
    const result = await saveAsCustomTheme(newThemeName.trim(), previewColors);
    if (result) {
      setCreateDialogOpen(false);
      setNewThemeName("");

      // Select the newly created theme
      if (result.themeId) {
        selectTheme(result.themeId);
        // Notify parent (e.g. ThemeLibrarySettings) to refresh the theme list
        onThemeSelect?.(result.themeId);
      }
    }
  }, [newThemeName, saveAsCustomTheme, toast, previewColors, selectTheme, onThemeSelect]);

  // Delete custom theme
  const handleDeleteTheme = useCallback(async () => {
    if (!selectedThemeId) return;

    setIsDeleting(true);
    try {
      const success = await deleteTheme(selectedThemeId);
      if (success) {
        setDeleteDialogOpen(false);
      }
    } finally {
      setIsDeleting(false);
    }
  }, [selectedThemeId, deleteTheme]);

  // Handle theme saved from AI generation (ThemePreviewCard)
  // Refreshes theme list and updates selected theme to the newly saved one
  const handleThemeSaved = useCallback(
    async (themeId: string) => {
      await loadThemes();
      selectTheme(themeId);
    },
    [loadThemes, selectTheme],
  );

  // Current mode styles
  const currentStyles = styles[mode] || {};

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col h-full bg-background">
        <Header onBack={handleBackClick} />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col h-full bg-background">
        <Header onBack={handleBackClick} />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background overflow-hidden">
      <Header
        onBack={handleBackClick}
        actions={
          <>
            <CopyableCell
              value={generateThemeCode(
                { light: styles.light, dark: styles.dark },
                "oklch",
              )}
              display={null}
              tooltipLabel="Copy CSS variables"
              iconSize={14}
              iconClassName="text-muted-foreground"
              buttonClassName="h-8 w-8 text-muted-foreground hover:text-foreground"
            />
            <Hint text="Create theme" delayDuration={0}>
              <Button
                variant="ghost"
                className="h-8 px-2 cursor-pointer text-muted-foreground hover:text-foreground"
                size="sm"
                onClick={() => setCreateDialogOpen(true)}
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </Hint>
          </>
        }
      />

      {/* Tabs - with ScrollArea */}
      <ScrollArea className="flex-1">
        <div className={`flex flex-col gap-4 ${isTablet ? "p-3" : "p-4"}`}>
          {/* Theme Preset Selection */}
          <div className="flex flex-col gap-3">
            <ThemePresetSelect
              themes={themes}
              currentPreset={selectedThemeId}
              onPresetChange={selectTheme}
              onCssImport={handleCssImport}
              isImportedTheme={isImportedTheme}
            />
          </div>

          {/* Mode Selection */}
          <div className="flex flex-col gap-2">
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Mode
            </h3>
            <ToggleGroup
              type="single"
              value={mode}
              onValueChange={handleModeChange}
              className="bg-muted p-1 rounded-lg"
            >
              <ToggleGroupItem
                value="light"
                aria-label="Toggle light"
                className="text-muted-foreground hover:text-foreground cursor-pointer rounded-md px-3 py-1.5 flex-1 data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
              >
                <Sun className="size-3.5 mr-1.5" />
                <span className="text-xs">Light</span>
              </ToggleGroupItem>
              <ToggleGroupItem
                value="dark"
                aria-label="Toggle dark"
                className="text-muted-foreground hover:text-foreground cursor-pointer rounded-md px-3 py-1.5 flex-1 data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
              >
                <Moon className="size-3.5 mr-1.5" />
                <span className="text-xs">Dark</span>
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-1">
            <Hint text="Undo (⌘Z)" delayDuration={0}>
              <Button
                variant="ghost"
                className="h-8 w-8 cursor-pointer text-muted-foreground hover:text-foreground"
                size="icon"
                onClick={undo}
                disabled={!canUndo}
              >
                <Undo2 className="h-3.5 w-3.5" />
              </Button>
            </Hint>
            <Hint text="Redo (⌘Y)" delayDuration={0}>
              <Button
                variant="ghost"
                className="h-8 w-8 cursor-pointer text-muted-foreground hover:text-foreground"
                size="icon"
                onClick={redo}
                disabled={!canRedo}
              >
                <Redo2 className="h-3.5 w-3.5" />
              </Button>
            </Hint>
            <Hint text="Reset to original" delayDuration={0}>
              <Button
                variant="ghost"
                className="h-8 px-2 cursor-pointer text-muted-foreground hover:text-foreground"
                size="sm"
                onClick={resetToOriginal}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                <span className="text-xs">Reset</span>
              </Button>
            </Hint>
            {selectedTheme?.type === "custom" && (
              <Hint text="Delete theme" delayDuration={0}>
                <Button
                  variant="ghost"
                  className="h-8 w-8 cursor-pointer text-destructive/70 hover:text-destructive hover:bg-destructive/10"
                  size="icon"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </Hint>
            )}
          </div>

          {/* Tabs: Colors, Typography, Other */}
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="mb-3 grid w-full grid-cols-3 bg-muted p-1 h-9">
              <TabsTrigger
                value="colors"
                className="cursor-pointer text-xs text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                Colors
              </TabsTrigger>
              <TabsTrigger
                value="typography"
                className="cursor-pointer text-xs text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                Typography
              </TabsTrigger>
              <TabsTrigger
                value="other"
                className="cursor-pointer text-xs text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                Other
              </TabsTrigger>
            </TabsList>

            {/* Colors Tab */}
            <TabsContent value="colors">
              <ThemeColorPanel
                currentTheme={currentStyles}
                onColorChange={handleStyleChange}
              />
            </TabsContent>

            {/* Typography Tab */}
            <TabsContent value="typography">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="font-sans" className="mb-1.5 block text-xs">
                    Sans-Serif Font
                  </Label>
                  <ThemeFontSelect
                    fonts={sansSerifFonts}
                    defaultValue={DEFAULT_FONT_SANS}
                    currentFont={getAppliedThemeFont(
                      currentStyles["font-sans"],
                      "font-sans",
                    )}
                    onFontChange={(value) =>
                      handleFontChange("font-sans", value)
                    }
                  />
                </div>
                {/* <div>
                    <Label
                      htmlFor="font-serif"
                      className="mb-1.5 block text-xs"
                    >
                      Serif Font
                    </Label>
                    <ThemeFontSelect
                      fonts={serifFonts}
                      defaultValue={DEFAULT_FONT_SERIF}
                      currentFont={getAppliedThemeFont(
                        currentStyles["font-serif"],
                        "font-serif",
                      )}
                      onFontChange={(value) =>
                        handleFontChange("font-serif", value)
                      }
                    />
                  </div>

                  <div>
                    <Label htmlFor="font-mono" className="mb-1.5 block text-xs">
                      Monospace Font
                    </Label>
                    <ThemeFontSelect
                      fonts={monoFonts}
                      defaultValue={DEFAULT_FONT_MONO}
                      currentFont={getAppliedThemeFont(
                        currentStyles["font-mono"],
                        "font-mono",
                      )}
                      onFontChange={(value) =>
                        handleFontChange("font-mono", value)
                      }
                    />
                  </div> */}
                <div className="pt-2">
                  <SliderWithInput
                    value={parseFloat(
                      currentStyles["letter-spacing"]?.replace("em", "") || "0",
                    )}
                    onChange={handleLetterSpacingChange}
                    min={-0.25}
                    max={0.25}
                    step={0.025}
                    unit="em"
                    label="Letter Spacing"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Other Tab */}
            <TabsContent value="other">
              <div className="space-y-4">
                <SliderWithInput
                  value={parseFloat(
                    currentStyles.radius?.replace("rem", "") || "0",
                  )}
                  onChange={handleRadiusChange}
                  min={0}
                  max={2.5}
                  step={0.025}
                  unit="rem"
                  label="Radius"
                />

                <SliderWithInput
                  value={parseFloat(
                    currentStyles.spacing?.replace("rem", "") || "0.25",
                  )}
                  onChange={handleSpacingChange}
                  min={0.15}
                  max={0.35}
                  step={0.01}
                  unit="rem"
                  label="Spacing"
                />

                <div className="pt-2">
                  <ShadowControl
                    shadowColor={colorFormatter(
                      currentStyles["shadow-color"] || "#000000",
                      "hex",
                    )}
                    shadowOpacity={parseFloat(
                      currentStyles["shadow-opacity"] || "0.1",
                    )}
                    shadowBlur={parseFloat(
                      currentStyles["shadow-blur"]?.replace("px", "") || "0",
                    )}
                    shadowSpread={parseFloat(
                      currentStyles["shadow-spread"]?.replace("px", "") || "0",
                    )}
                    shadowOffsetX={parseFloat(
                      currentStyles["shadow-offset-x"]?.replace("px", "") ||
                        "0",
                    )}
                    shadowOffsetY={parseFloat(
                      currentStyles["shadow-offset-y"]?.replace("px", "") ||
                        "0",
                    )}
                    onChange={handleShadowChange}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </ScrollArea>

      {/* Fixed Apply Button - hidden in standalone mode.
          Imported themes (from AI HITL Edit) and scratch themes both lack a
          themeId — both route to the save dialog so the user can name and
          persist them as a new custom theme, then apply to the project. */}
      {!standaloneMode && (
        <div className="border-t border-border p-4 bg-background">
          <Button
            className="w-full gap-2"
            onClick={
              isScratchTheme || isImportedTheme
                ? () => {
                    setSaveDialogOpen(true);
                  }
                : handleApplyToProject
            }
            disabled={
              isApplying ||
              (!selectedThemeId && !isScratchTheme && !isImportedTheme)
            }
          >
            {isApplying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Applying...
              </>
            ) : isScratchTheme || isImportedTheme || hasChanges ? (
              "Save & Apply"
            ) : (
              "Apply to Project"
            )}
          </Button>
        </div>
      )}

      {/* Save Changes Button - shown in standalone mode when there are changes */}
      {standaloneMode && hasUnsavedChanges && (
        <div className="border-t border-border p-4 bg-background">
          <Button
            className="w-full gap-2"
            onClick={() => {
              // If editing a custom theme, update it directly
              if (selectedTheme?.type === "custom" && selectedThemeId) {
                handleSaveChangesToCustomTheme();
              } else {
                // For built-in themes or scratch, open save dialog to create new
                setSaveDialogOpen(true);
              }
            }}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : selectedTheme?.type === "custom" ? (
              "Save Changes"
            ) : (
              "Save as Custom Theme"
            )}
          </Button>
        </div>
      )}

      {/* Save Theme Dialog */}
      <SaveThemeDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        themeName={newThemeName}
        onThemeNameChange={setNewThemeName}
        previewColors={previewColors}
        onColorChange={handleColorChange}
        onShuffleColors={handleShuffleColors}
        onSave={handleSaveAsCustom}
        isSaving={isSaving}
        isApplying={isApplying}
      />

      {/* Delete Theme Confirmation Dialog */}
      <DeleteThemeDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        themeName={selectedTheme?.name || ""}
        onDelete={handleDeleteTheme}
        isDeleting={isDeleting}
      />

      {/* Create Theme Dialog */}
      <CreateThemeDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        themeName={newThemeName}
        onThemeNameChange={setNewThemeName}
        previewColors={previewColors}
        onColorChange={handleColorChange}
        onShuffleColors={handleShuffleColors}
        onCreate={handleCreateTheme}
        isCreating={isSaving}
      />

      {/* Unsaved Changes Confirmation Dialog */}
      <AlertDialog
        open={unsavedChangesDialogOpen}
        onOpenChange={setUnsavedChangesDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to your theme. Are you sure you want to
              close without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCloseWithoutSaving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Close Without Saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Header component
function Header({
  onBack,
  actions,
  title = "Themes",
}: {
  onBack?: () => void;
  actions?: React.ReactNode;
  title?: string;
}) {
  return (
    <div className="h-12 px-4 border-b border-border flex items-center justify-between bg-background">
      {onBack ? (
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-muted-foreground transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>{title}</span>
        </button>
      ) : (
        <span className="text-sm font-medium text-foreground">{title}</span>
      )}
      {actions && <div className="flex items-center gap-1">{actions}</div>}
    </div>
  );
}
